import { currencyFractionDigits } from '../../../shared/utils/commercial-money';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { finalize } from 'rxjs';
import { AccountsService } from '../../accounts.service';
import { AccountPayables } from '../payables.model';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageActionPaneComponent } from '../../../shared/page-layout/page-action-pane.component';
import { CrmPartyContextService } from '../../../shared/crm-client-contact-picker/crm-party-context.service';

@Component({
    selector: 'app-payable-detail',
    standalone: true,
    imports: [CommonModule, ButtonModule, TableModule, TabsModule, TagModule, ToastModule, PageShellComponent, PageDetailHeroComponent, PageDataPanelComponent, PageActionPaneComponent],
    providers: [MessageService],
    template: `
        <app-page-shell>
            <p-toast position="top-center"></p-toast>
            @if (payable) {
                <app-page-detail-hero [auditStamp]="payable" eyebrow="Account Payable"
                    [title]="displayDocumentReference(payable.payableReferenceNumber)"
                    [subtitle]="crmPartyContext.displayName(payable) || 'Vendor payable staging document'"
                    icon="pi pi-credit-card">
                    <div tags>
                        <p-tag [value]="payable.status || 'UNKNOWN'" [severity]="statusSeverity(payable.status)"></p-tag>
                        <p-tag [value]="payable.sourceType || 'SYSTEM'" severity="secondary"></p-tag>
                        <p-tag [value]="formatMoney(payable.amount)" severity="info"></p-tag>
                    </div>
                </app-page-detail-hero>

                <div class="document-detail-layout">
                <div class="document-detail-content">
                <p-tabs [value]="activeTab" (valueChange)="onTabChange($event)">
                    <p-tablist>
                        <p-tab value="overview"><i class="pi pi-info-circle"></i> Overview</p-tab>
                        <p-tab value="lines"><i class="pi pi-list"></i> Lines</p-tab>
                        <p-tab value="related"><i class="pi pi-share-alt"></i> Related</p-tab>
                    </p-tablist>
                    <p-tabpanels>
                        <p-tabpanel value="overview">
                            <app-page-data-panel title="Payable Summary">
                                <div class="detail-grid">
                                    <div><strong>CRM Vendor</strong><span>{{ crmPartyContext.displayName(payable) || '-' }}</span></div>
                                    <div><strong>CRM Contact</strong><span>{{ crmPartyContext.contactDisplayName(payable) || '-' }}</span></div>
                                    <div><strong>Total</strong><span>{{ formatMoney(payable.amount) }}</span></div>
                                    <div><strong>Tax</strong><span>{{ formatMoney(payable.taxAmount) }}</span></div>
                                    <div><strong>Net amount</strong><span>{{ formatMoney(payable.amountExclTax) }}</span></div>
                                    <div><strong>Bill status</strong><span>{{ payable.billNumber || 'Not billed' }}</span></div>
                                    <div><strong>Billing date</strong><span>{{ payable.billingDate | date:'yyyy-MM-dd' }}</span></div>
                                    <div><strong>Expected payment</strong><span>{{ payable.expectedPaymentDate | date:'yyyy-MM-dd' }}</span></div>
                                    <div><strong>Created</strong><span>{{ payable.createdAt | date:'medium' }}</span></div>
                                </div>
                            </app-page-data-panel>

                        </p-tabpanel>
                        <p-tabpanel value="lines">
                            <app-page-data-panel title="Payable Lines" [resultText]="(payable.accountPayablesLines?.length || 0) + ' lines'">
                                <p-table [value]="payable.accountPayablesLines || []" responsiveLayout="scroll">
                                    <ng-template pTemplate="header"><tr><th>Description</th><th>Source</th><th>Qty</th><th class="numeric">Net</th><th class="numeric">VAT</th><th class="numeric">Total</th></tr></ng-template>
                                    <ng-template pTemplate="body" let-line><tr><td>{{ line.lineDescription || '—' }} <small class="internal-type">{{ line.payablesLineType || 'MISCELLANEOUS' }}</small></td><td>{{ compactReference(line.payableLineRefNumber || payable.sourceResourceNumber || payable.sourceReference) }}</td><td>{{ line.quantity || 1 }}</td><td class="numeric">{{ formatMoney(line.amountExclTax) }}</td><td class="numeric">{{ formatMoney(line.taxAmount) }}</td><td class="numeric">{{ formatMoney(line.amount) }}</td></tr></ng-template>
                                    <ng-template pTemplate="emptymessage"><tr><td colspan="6">No payable lines are available.</td></tr></ng-template>
                                </p-table>
                            </app-page-data-panel>
                        </p-tabpanel>
                        <p-tabpanel value="related">
                            <app-page-data-panel title="Source and Review Context">
                                <div class="detail-grid">
                                    <div><strong>Source</strong><span>{{ compactReference(payable.sourceResourceNumber || payable.sourceReference) }}</span></div>
                                    <div><strong>External reference</strong><span>{{ payable.externalReferenceNumber || '-' }}</span></div>
                                    <div><strong>Technical source</strong><span>{{ payable.sourceResourceType || '-' }} / {{ payable.sourceResourceId || '-' }}</span></div>
                                    <div><strong>Source module</strong><span>{{ payable.createdFromModule || payable.payableTxnType || '-' }}</span></div>
                                    <div><strong>Manual reason</strong><span>{{ payable.manualReasonText || payable.manualReasonCode || '-' }}</span></div>
                                    <div><strong>Hold reason</strong><span>{{ payable.holdReason || '-' }}</span></div>
                                    <div><strong>Match exception</strong><span>{{ payable.matchExceptionReason || '-' }}</span></div>
                                    <div><strong>Review reason</strong><span>{{ payable.reviewReason || '-' }}</span></div>
                                </div>
                            </app-page-data-panel>
                        </p-tabpanel>
                    </p-tabpanels>
                </p-tabs>
                </div>
                <app-page-action-pane title="Payable Actions" subtitle="Open linked documents and review the source context.">
                    <div class="action-buttons">
                        <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (click)="back()"></p-button>
                        @if (payable.billId) {
                            <p-button label="View Bill" icon="pi pi-file" severity="info" [outlined]="true" (click)="viewBill()"></p-button>
                        }
                        <p-button label="View Source" icon="pi pi-external-link" severity="secondary" [outlined]="true" (click)="viewSource()"></p-button>
                    </div>
                </app-page-action-pane>
                </div>
            } @else if (!isLoading) {
                <app-page-data-panel title="Payable not found" subtitle="The requested payable is unavailable or you no longer have access to it.">
                    <p-button label="Back to Payables" icon="pi pi-arrow-left" (click)="back()"></p-button>
                </app-page-data-panel>
            }
        </app-page-shell>
    `,
    styles: [`
        .detail-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: .75rem; }
        .detail-grid div { display: flex; flex-direction: column; gap: .25rem; padding: .75rem; border: 1px solid #e2e8f0; border-radius: 8px; }
        .detail-grid span { color: #475569; overflow-wrap: anywhere; }
        .numeric { text-align: right; }
        .internal-type { display: block; color: #64748b; font-size: .72rem; margin-top: .2rem; }
        .document-detail-layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(20rem, 22rem); gap: 1rem; align-items: start; }
        .document-detail-content { min-width: 0; }
        :host ::ng-deep .p-tab { gap: .45rem; }
        @media (max-width: 1080px) { .document-detail-layout { grid-template-columns: 1fr; } }
    `]
})
export class PayableDetailComponent implements OnInit {
    payable?: AccountPayables;
    activeTab = 'overview';
    isLoading = true;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly accountsService: AccountsService,
        private readonly messageService: MessageService,
        public readonly crmPartyContext: CrmPartyContextService
    ) {}

    ngOnInit(): void {
        this.route.queryParamMap.subscribe(params => this.activeTab = this.resolveTab(params.get('tab')));
        const id = Number(this.route.snapshot.paramMap.get('id'));
        if (!id) {
            this.isLoading = false;
            return;
        }
        this.accountsService.getAccountPayableById(id).pipe(finalize(() => this.isLoading = false)).subscribe({
            next: payable => this.payable = payable,
            error: error => this.messageService.add({ severity: 'error', summary: 'Unable to load payable', detail: error?.error?.message || 'The payable could not be loaded.' })
        });
    }

    onTabChange(value: string | number): void {
        this.activeTab = this.resolveTab(String(value));
        this.router.navigate([], { relativeTo: this.route, queryParams: { tab: this.activeTab }, queryParamsHandling: 'merge', replaceUrl: true });
    }

    back(): void { this.router.navigate(['/accounts/enterprise/payables/to-be-billed']); }

    viewBill(): void {
        if (this.payable?.billId) this.router.navigate(['/accounts/enterprise/payables/generated-bills/view', this.payable.billId]);
    }

    viewSource(): void {
        const sourceId = this.payable?.sourceResourceId || this.payable?.accountPayablesLines?.[0]?.headerReferenceId;
        if (!sourceId) return this.showSourceUnavailable();
        switch (this.payable?.payableTxnType) {
            case 'SHIPMENT': this.router.navigate(['/shipment/view', sourceId]); break;
            case 'CLEARANCE': this.router.navigate(['/clearance/view', sourceId]); break;
            case 'ORDER': this.router.navigate(['/order/purchase-order/view', sourceId]); break;
            default: this.showSourceUnavailable();
        }
    }

    formatMoney(value: number | undefined): string {
        return `${this.payable?.country?.currencyCode || 'SAR'} ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: currencyFractionDigits(this.payable?.country?.currencyCode || 'SAR'), maximumFractionDigits: currencyFractionDigits(this.payable?.country?.currencyCode || 'SAR') })}`;
    }

    compactReference(value: string | undefined): string {
        if (!value) return '—';
        const operationalNumber = value.match(/(?:SHP|DLR|SO|PO|CLR)\/\d{4}\/\d{2}\/\d+/)?.[0];
        return operationalNumber || (value.length > 44 ? `${value.slice(0, 41)}…` : value);
    }

    displayDocumentReference(value: string | undefined): string {
        if (!value) return 'Payable';
        if (/^AP\/\d{4}\/\d{2}\/\d{8}$/.test(value)) return value;
        return this.compactReference(value);
    }

    statusSeverity(status: string | undefined): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'BILLED') return 'success';
        if (status === 'ON_HOLD' || status === 'APPROVAL_REQUIRED') return 'warn';
        if (status === 'MATCH_EXCEPTION') return 'danger';
        if (status === 'NEW' || status === 'READY' || status === 'DRAFT' || status === 'CAPTURED' || status === 'APPROVED') return 'info';
        return 'secondary';
    }

    private resolveTab(tab: string | null): string { return ['overview', 'lines', 'related'].includes((tab || '').toLowerCase()) ? (tab || '').toLowerCase() : 'overview'; }
    private showSourceUnavailable(): void { this.messageService.add({ severity: 'warn', summary: 'Source unavailable', detail: 'No supported source document is linked to this payable.' }); }
}
